//
// Copyright (c) 2021 Astute Global, Inc.
//

import { Plugin, toWidgetEditable } from "ckeditor5";
import PrivilegedAreaCommand from "../privilegedArea/privilegedAreaCommand";
import { PLACEHOLDER_VALUE } from "../ckEditorConstants";
import { areaIdsToAreaNames } from "./util";

// noinspection SpellCheckingInspection
const ATTRIBUTE_PRIVILEGE_IDS = "data-rdprivileges";
// noinspection SpellCheckingInspection
const ATTRIBUTE_PRIVILEGE_NAMES = "data-rdprivileges-name";
const MODEL_ATTRIBUTE_AREA_IDS = "areaIds";
const MODEL_ATTRIBUTE_AREA_NAMES = "areaNames";

export default class PrivilegedAreaEditing extends Plugin {
  // noinspection JSUnusedGlobalSymbols
  static get requires() {
    return [];
  }

  // noinspection JSUnusedGlobalSymbols
  static get pluginName() {
    return "PrivilegedAreaEditing";
  }

  init() {
    const editor = this.editor;
    this._setupConfig();
    this._setupSchema();
    this._setupConversions();
    editor.commands.add("privilegedArea", new PrivilegedAreaCommand(editor));
  }

  // noinspection JSUnusedGlobalSymbols
  afterInit() {
    this._setupEnterCommand();
  }

  _createPrivilegedAreaAttributes(modelItem) {
    const areaIds = modelItem.getAttribute(MODEL_ATTRIBUTE_AREA_IDS);
    if (!areaIds || areaIds == PLACEHOLDER_VALUE) {
      return null;
    }
    const privilegedAreaIdArray = areaIds.split(",");
    if (privilegedAreaIdArray.length <= 0) {
      return null;
    }
    let privilegedAreaNames = modelItem.getAttribute(MODEL_ATTRIBUTE_AREA_NAMES);
    if (!privilegedAreaNames || privilegedAreaNames == PLACEHOLDER_VALUE) {
      privilegedAreaNames = areaIdsToAreaNames(this.editor, privilegedAreaIdArray);
    }
    const result = {};
    result[ATTRIBUTE_PRIVILEGE_IDS] = privilegedAreaIdArray.join(",");
    result[ATTRIBUTE_PRIVILEGE_NAMES] = privilegedAreaNames;
    return result;
  }

  _setupConfig() {
    this.editor.config.define("privilegedArea", {
      invalidRoleName: "Invalid Role",
      placeholderName: "Privileged Area content",
    });
  }

  _setupConversions() {
    const conversion = this.editor.conversion;
    conversion.attributeToAttribute({
      view: ATTRIBUTE_PRIVILEGE_IDS,
      model: MODEL_ATTRIBUTE_AREA_IDS,
    });
    conversion.attributeToAttribute({
      view: ATTRIBUTE_PRIVILEGE_NAMES,
      model: MODEL_ATTRIBUTE_AREA_NAMES,
    });
    conversion.for("upcast").elementToElement({
      view: {
        name: "div",
        attributes: [ATTRIBUTE_PRIVILEGE_IDS],
      },
      model: (viewElement, { writer }) => {
        const privilegedAreaIdCsv = viewElement.getAttribute(ATTRIBUTE_PRIVILEGE_IDS);
        let privilegedAreaNameCsv = viewElement.getAttribute(ATTRIBUTE_PRIVILEGE_NAMES);
        if (!privilegedAreaNameCsv || privilegedAreaNameCsv == PLACEHOLDER_VALUE) {
          privilegedAreaNameCsv = areaIdsToAreaNames(this.editor, privilegedAreaIdCsv);
        }
        return writer.createElement("privilegedArea", {
          areaIds: privilegedAreaIdCsv,
          areaNames: privilegedAreaNameCsv,
        });
      },
    });
    conversion.for("dataDowncast").elementToElement({
      model: "privilegedArea",
      view: (modelItem, { writer }) =>
        writer.createContainerElement("div", this._createPrivilegedAreaAttributes(modelItem)),
    });
    conversion.for("editingDowncast").elementToElement({
      model: "privilegedArea",
      view: (modelItem, { writer }) => {
        return toWidgetEditable(
          writer.createEditableElement("div", this._createPrivilegedAreaAttributes(modelItem)),
          writer,
        );
      },
    });
  }

  _setupEnterCommand() {
    // Enter at the end of a privileged area should create a paragraph.
    const editor = this.editor;
    const enterCommand = editor.commands.get("enter");
    if (enterCommand) {
      this.listenTo(enterCommand, "afterExecute", (_evt, data) => {
        const positionParent = editor.model.document.selection.getFirstPosition().parent;
        const isPrivilegedArea = positionParent.is("element", "privilegedArea");
        if (
          isPrivilegedArea &&
          !positionParent.is("element", "paragraph") &&
          positionParent.childCount === 0
        ) {
          data.writer.rename(positionParent, "paragraph");
        }
      });
    }
  }

  _setupSchema() {
    this.editor.model.schema.register("privilegedArea", {
      allowAttributes: [MODEL_ATTRIBUTE_AREA_IDS, MODEL_ATTRIBUTE_AREA_NAMES],
      allowContentOf: "$root",
      allowWhere: "$blockObject",
      inheritTypesFrom: "$blockObject",
    });
  }
}
